import UseContext from "../../../../../hooks/useContext";
import useModal from "../../../useModal";
import Modalheader from "../../../components/header";
import React, { useMemo, useState } from "react";
import ModalFooter from "../../../components/footer";
import Button from "../../../../button";
import ModalBody from "../../../components/body";
import Banner from "../../../../banner/banner";
import CheckboxOptionCard from "../../../../checkboxOptionCard/CheckboxOptionCard";
import BadgeSquare from "../../../../badge/badgeSquare";
import { ChevronDown, ChevronTop } from "../../../../../assets/icons";
import ApiDatabase from "server";
import { toast } from "react-toastify";
import { ScheduleChangeBeforeAfter } from "../../../../scheduleCompact/ScheduleCompactDisplay";

const FIELD_LABELS = {
  qualification: "Qualification",
  period: "Période",
  schedule: "Planning",
  salary: "Rémunération",
  address: "Adresse",
  reason: "Motif",
  licences: "Permis",
  languages: "Langue",
  tools: "Matériel",
  reference: "Référence",
  other: "Informations pour le candidat",
  other_interim: "Informations pour l'agence",
};

function formatPeriod(val) {
  if (!val) return "";
  if (val.start && val.end) {
    const s = new Date(val.start).toLocaleDateString("fr-FR");
    const e = new Date(val.end).toLocaleDateString("fr-FR");
    return `${s} → ${e}`;
  }
  return String(val);
}

const AMENDMENT_STATUS_MAP = {
  accepted: { type: "10", label: "Accepté" },
  refused: { type: "3", label: "Refusé" },
  replaced: { type: "18", label: "Remplacée" },
  pending: { type: "20", label: "En attente" },
};

function formatChangeValue(field, value) {
  if (field === "period" && typeof value === "object" && value !== null) {
    const start = value.start
      ? new Date(value.start).toLocaleDateString("fr-FR")
      : "?";
    const end = value.end
      ? new Date(value.end).toLocaleDateString("fr-FR")
      : "?";
    return `${start} – ${end}`;
  }
  if (field === "schedule" && Array.isArray(value)) return "—";
  if (typeof value === "object" && value !== null) return JSON.stringify(value);
  return String(value ?? "");
}

function AmendmentRequestItemCompact({ request }) {
  const [open, setOpen] = useState(false);
  const statusCfg =
    AMENDMENT_STATUS_MAP[request.status] || AMENDMENT_STATUS_MAP.pending;

  return (
    <div className="border rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full py-2 px-3 flex justify-between items-center text-xs hover:bg-gray-50 transition-colors"
      >
        <div className="flex gap-2 items-center">
          <span>
            Demande du{" "}
            {new Date(request.createdAt).toLocaleDateString("fr-FR")}
          </span>
          <BadgeSquare
            type={statusCfg.type}
            customLabel={statusCfg.label}
            size="xs"
          />
        </div>
        {open ? (
          <ChevronTop wh={16} color="#374151" />
        ) : (
          <ChevronDown wh={16} color="#374151" />
        )}
      </button>
      {open && (
        <div className="px-3 pb-2 flex flex-col gap-1 border-t bg-gray-50">
          {request.changes?.map((c, i) => (
            <div
              key={i}
              className={`flex gap-2 text-xs pt-1.5 ${
                c.field === "schedule"
                  ? "flex-col items-stretch"
                  : "items-center"
              }`}
            >
              <span className="font-medium text-gray-700">
                {FIELD_LABELS[c.field] || c.field}
              </span>
              {c.field === "schedule" ? (
                <ScheduleChangeBeforeAfter before={c.before} after={c.after} />
              ) : (
                <>
                  <span className="text-gray-500">
                    {formatChangeValue(c.field, c.before)}
                  </span>
                  <span className="text-gray-400">→</span>
                  <span className="text-gray-900 font-medium">
                    {formatChangeValue(c.field, c.after)}
                  </span>
                </>
              )}
            </div>
          ))}
          {request.respondedAt && (
            <div className="text-gray-400 text-xs pt-1">
              Répondu le{" "}
              {new Date(request.respondedAt).toLocaleDateString("fr-FR")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function getMissionTemporalStatus(mission) {
  const today = new Date().toISOString().substring(0, 10);
  const start = new Date(mission.duration?.datestart)
    .toISOString()
    .substring(0, 10);
  const end = new Date(mission.duration?.dateend)
    .toISOString()
    .substring(0, 10);
  if (today < start) return "upcoming";
  if (today > end) return "past";
  return "in_progress";
}

function computeCheckResult(mission, changes) {
  if (!mission || !changes?.length) return null;

  const hasContract =
    mission.contract_tempworker?.length > 0 &&
    mission.contract_tempworker.some((c) => c.id_bestt);
  const isAssigned = !!mission.user_assigned;
  const hasPeriodChange = changes.some((c) => c.field === "period");

  if (!isAssigned || !hasContract) {
    return {
      canConfirm: true,
      noContract: isAssigned && !hasContract,
    };
  }

  if (!hasPeriodChange) {
    return {
      canConfirm: true,
      banner: {
        type: "warning",
        message:
          "Le dernier contrat/avenant sera re-généré et renvoyé pour signature avec vos modifications.",
      },
    };
  }

  const temporalStatus = getMissionTemporalStatus(mission);

  if (temporalStatus === "upcoming") {
    return {
      canConfirm: true,
      banner: {
        type: "warning",
        message:
          "Le dernier contrat/avenant sera re-généré et renvoyé pour signature avec vos modifications.",
      },
    };
  }

  const periodChange = changes.find((c) => c.field === "period");
  const newDateEnd = new Date(periodChange.after.end);

  const re = mission.replacement_employee;
  if (re && (re.firstname || re.lastname || re.poste || re.description)) {
    return {
      canConfirm: false,
      banner: {
        type: "alert",
        message:
          'Le motif étant "Remplacement d\'un salarié absent", la date de fin de la mission n\'est pas modifiable.',
      },
    };
  }

  if (mission.flexibility?.used) {
    return {
      canConfirm: false,
      canDuplicate: true,
      duplicateReason: "flexibility_exhausted",
      banner: {
        type: "alert",
        message:
          "La souplesse a déjà été utilisée, la date de fin ne peut pas être modifiée.",
      },
    };
  }

  if (mission.flexibility?.start && mission.flexibility?.end) {
    const flexEnd = new Date(mission.flexibility.end);
    if (newDateEnd <= flexEnd) {
      return {
        canConfirm: true,
        banner: {
          type: "info",
          message:
            "La date de fin se trouvant dans la souplesse positive, aucun avenant ne sera créé pour cette mission.",
        },
      };
    }
  }

  const maxAvenantNumber = Math.max(
    0,
    ...(mission.contract_tempworker || []).map((c) => c.avenant_number || 0)
  );
  if (maxAvenantNumber >= 2) {
    return {
      canConfirm: false,
      canDuplicate: true,
      duplicateReason: "max_amendments",
      banner: {
        type: "alert",
        borderless: true,
        message:
          "Le nombre maximum d'avenants (2) a été atteint. Vous pouvez dupliquer la mission.",
      },
    };
  }

  const missionStart = new Date(mission.duration?.datestart);
  const monthsDiff =
    (newDateEnd.getFullYear() - missionStart.getFullYear()) * 12 +
    (newDateEnd.getMonth() - missionStart.getMonth());
  if (monthsDiff > 18) {
    return {
      canConfirm: false,
      canDuplicate: true,
      duplicateReason: "max_duration",
      banner: {
        type: "alert",
        message:
          "Les dates de la mission dépassent les 18 mois, la date de fin ne peut pas être modifiée.",
      },
    };
  }

  // Check if the latest contract/amendment has been signed
  const latestContract = [...(mission.contract_tempworker || [])]
    .sort((a, b) => (b.avenant_number || 0) - (a.avenant_number || 0))[0];
  const isLatestSigned =
    latestContract?.status === 1 || latestContract?.status === "1";

  if (!isLatestSigned) {
    return {
      canConfirm: true,
      banner: {
        type: "warning",
        message:
          "Le dernier contrat/avenant n'a pas encore été signé. Il sera re-généré avec les nouvelles dates.",
      },
    };
  }

  return { canConfirm: true, isAmendmentRequest: true };
}

const ModalMissionDetailSubmit = () => {
  const { toggle } = useModal();
  const {
    listChangesMissionDetail,
    isChangesNeedsNewContractMissionDetail,
    infoMission,
    objID,
    setObjID,
    setModalDataReturn,
  } = UseContext();
  const [isLoading, setIsLoading] = useState(false);
  const [reinviteUser, setReinviteUser] = useState(false);
  const [generateContractAfter, setGenerateContractAfter] = useState(false);
  const token = localStorage.getItem("xsrfToken");

  const checkResult = useMemo(
    () => computeCheckResult(infoMission, listChangesMissionDetail),
    [infoMission, listChangesMissionDetail]
  );

  const canConfirm = checkResult?.canConfirm !== false;
  const canDuplicate = checkResult?.canDuplicate === true;
  const banner = checkResult?.banner;

  const pendingRequest = useMemo(
    () => infoMission?.amendments?.find((a) => a.status === "pending"),
    [infoMission?.amendments]
  );

  const handleSubmit = () => {
    setIsLoading(true);
    ApiDatabase.postMissionDetail(
      {
        token,
        idMission: objID,
        generateContract: isChangesNeedsNewContractMissionDetail || generateContractAfter,
        changes: listChangesMissionDetail,
      },
      (data) => {
        toast.success(data.message || data.error || data.success);
        if (data.idContract) {
          setObjID(data.idContract);
        } else {
          setObjID("");
        }
        setModalDataReturn("mission updated");
        toggle();
      },
      (err) => {
        toast.error(err.response?.data?.message || err.response?.data?.error);
        setModalDataReturn("mission updated");
        toggle();
      }
    );
  };

  const handleDuplicate = () => {
    setIsLoading(true);
    const originalEnd = new Date(infoMission?.duration?.dateend);
    const dayAfter = new Date(originalEnd);
    dayAfter.setDate(dayAfter.getDate() + 1);

    ApiDatabase.postDuplicateMission(
      {
        token,
        idMission: objID,
        dateStart: dayAfter.toISOString(),
        dateEnd: listChangesMissionDetail.find((c) => c.field === "period")
          ?.after?.end,
        reinviteUser,
        reason: checkResult?.duplicateReason || "other",
      },
      (data) => {
        toast.success(data.success || "Mission dupliquée");
        setModalDataReturn("mission updated");
        toggle();
      },
      (err) => {
        toast.error(err.response?.data?.message || "Erreur");
        setIsLoading(false);
      }
    );
  };

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        Confirmer la modification
      </Modalheader>
      <ModalBody>
        <div className="text-sm text-gray-500 flex flex-col gap-3">
          <div>
            Êtes-vous sûr de vouloir modifier la mission ?
          </div>

          {infoMission?.replacement_mission && (
            <Banner type="info">
              Cette mission a déjà été dupliquée (mission n°
              {infoMission.replacement_mission_info?.id_mission ||
                infoMission.replacement_mission}
              ).
            </Banner>
          )}

          {banner && (
            <Banner type={banner.type} borderless={banner.borderless}>
              {banner.message}
            </Banner>
          )}

          {checkResult?.noContract && (
            <>
              <Banner type="info">
                Aucun contrat BeSTT n'a été généré pour cette mission.
              </Banner>
              <CheckboxOptionCard
                id="modal-generate-contract-bestt"
                checked={generateContractAfter}
                onChange={(e) => setGenerateContractAfter(e.target.checked)}
                title="Générer le contrat BeSTT"
                description="Un contrat sera généré automatiquement après la modification."
              />
            </>
          )}

          {!banner && !checkResult?.isAmendmentRequest && !checkResult?.noContract && (
            <div className="text-gray-400 text-xs">
              Les modifications seront visibles par les candidats.
            </div>
          )}

          {checkResult?.isAmendmentRequest && (
            <>
              <Banner type="info">
                Une demande de prolongation sera envoyée à l'intérimaire pour
                validation.
                {pendingRequest &&
                  " La demande précédente en attente sera annulée."}
              </Banner>
              {pendingRequest && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-600">
                    Demande en cours qui sera remplacée :
                  </span>
                  <AmendmentRequestItemCompact request={pendingRequest} />
                </div>
              )}
            </>
          )}

          {listChangesMissionDetail.map((change, index) => (
            <div key={index} className="bg-gray-100 px-5 py-4 rounded flex flex-col gap-1">
              <span className="font-medium text-gray-700">
                {FIELD_LABELS[change.field] || change.field}
              </span>
              {change.field === "period" ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="line-through text-gray-400">
                    {formatPeriod(change.before)}
                  </span>
                  <span className="text-gray-400">→</span>
                  <span className="text-gray-800">
                    {formatPeriod(change.after)}
                  </span>
                </div>
              ) : change.field === "schedule" ? (
                <ScheduleChangeBeforeAfter
                  before={change.before}
                  after={change.after}
                />
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  {change.before && (
                    <>
                      <span className="line-through text-gray-400">
                        {typeof change.before === "object"
                          ? JSON.stringify(change.before)
                          : String(change.before)}
                      </span>
                      <span className="text-gray-400">→</span>
                    </>
                  )}
                  <span className="text-gray-800">
                    {typeof change.after === "object"
                      ? JSON.stringify(change.after)
                      : String(change.after)}
                  </span>
                </div>
              )}
            </div>
          ))}

          {canDuplicate && (
            <CheckboxOptionCard
              id="modal-reinvite-interim"
              checked={reinviteUser}
              onChange={(e) => setReinviteUser(e.target.checked)}
              title="Réinviter l'intérimaire"
              description="L'intérimaire actuel sera assigné à la nouvelle mission dupliquée."
            />
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        {isLoading ? (
          <>
            <Button size="LG" color="DISABLED">
              Annuler
            </Button>
            <Button size="LG" color="DISABLED">
              {canDuplicate
                ? "Duplication en cours ..."
                : "Modification de la mission ..."}
            </Button>
          </>
        ) : (
          <>
            <Button size="LG" color="SECONDARY" onClick={toggle}>
              Annuler
            </Button>
            {canDuplicate ? (
              <Button size="LG" color="PRIMARY" onClick={handleDuplicate}>
                Dupliquer la mission
              </Button>
            ) : (
              <Button
                size="LG"
                color={canConfirm ? "PRIMARY" : "DISABLED"}
                onClick={canConfirm ? handleSubmit : undefined}
              >
                Confirmer
              </Button>
            )}
          </>
        )}
      </ModalFooter>
    </>
  );
};

export default ModalMissionDetailSubmit;
